'use client';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/ui-kit/utils';

interface Tab {
  id: string;
  label: string;
}

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: Tab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  tabClassName?: string;
  hoverHighlight?: boolean;
  activeTabClassName?: string;
  hideBottomBorder?: boolean;
  activeIndicatorClassName?: string;
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      className,
      tabs,
      activeTab,
      onTabChange,
      tabClassName,
      hoverHighlight = false,
      activeTabClassName,
      activeIndicatorClassName,
      hideBottomBorder,
      ...props
    },
    ref,
  ) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [hoverStyle, setHoverStyle] = useState({});
    const [activeStyle, setActiveStyle] = useState({ left: '0px', width: '0px' });
    const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Find active index from activeTab prop
    const activeIndex = activeTab !== undefined ? tabs.findIndex((tab) => tab.id === activeTab) : 0;

    useEffect(() => {
      if (hoveredIndex !== null) {
        const hoveredElement = tabRefs.current[hoveredIndex];
        if (hoveredElement) {
          const { offsetLeft, offsetWidth } = hoveredElement;
          setHoverStyle({
            left: `${offsetLeft}px`,
            width: `${offsetWidth}px`,
          });
        }
      }
    }, [hoveredIndex]);

    useEffect(() => {
      const activeElement = tabRefs.current[activeIndex];
      if (activeElement) {
        const { offsetLeft, offsetWidth } = activeElement;
        setActiveStyle({
          left: `${offsetLeft}px`,
          width: `${offsetWidth}px`,
        });
      }
    }, [activeIndex]);

    useEffect(() => {
      requestAnimationFrame(() => {
        const firstElement = tabRefs.current[activeIndex >= 0 ? activeIndex : 0];
        if (firstElement) {
          const { offsetLeft, offsetWidth } = firstElement;
          setActiveStyle({
            left: `${offsetLeft}px`,
            width: `${offsetWidth}px`,
          });
        }
      });
    }, [activeIndex]);

    return (
      <div ref={ref} className={cn('relative w-full min-w-0', className)} {...props}>
        {/* Bottom border sits on the outer (non-scrolling) wrapper so it always
            spans the full visible width, not just the scrollable content width */}
        {!hideBottomBorder && (
          <div className="bg-srfc-3 absolute bottom-0 left-0 right-0 h-[1px]" />
        )}
        <div className="scrollbar-hide relative overflow-x-auto pb-[6px]">
          {/* Hover Highlight (optional) */}
          {hoverHighlight && (
            <div
              className="bg-srfc-3 absolute flex h-[30px] items-center rounded-[6px] transition-all duration-300 ease-out"
              style={{
                ...hoverStyle,
                opacity: hoveredIndex !== null ? 1 : 0,
              }}
            />
          )}

          {/* Active Indicator */}
          <div
            className={cn(
              'bg-foreground absolute bottom-0 z-10 h-[1px] transition-all duration-300 ease-out',
              activeIndicatorClassName,
            )}
            style={activeStyle}
          />

          {/* Tabs */}
          <div className="relative flex items-center space-x-[6px] whitespace-nowrap">
            {tabs.map((tab, index) => (
              <div
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                className={cn(
                  'h-[30px] cursor-pointer px-3 py-2 transition-colors duration-300',
                  index === activeIndex
                    ? cn('text-foreground', activeTabClassName)
                    : 'text-[#0e0f1199] dark:text-[#ffffff99]',
                )}
                onMouseEnter={() => {
                  if (!hoverHighlight) return;
                  setHoveredIndex(index);
                }}
                onMouseLeave={() => {
                  if (!hoverHighlight) return;
                  setHoveredIndex(null);
                }}
                onClick={() => {
                  onTabChange?.(tab.id);
                  const el = tabRefs.current[index];
                  const scrollParent = el?.closest('.overflow-x-auto') as HTMLElement | null;
                  if (el && scrollParent) {
                    const targetScroll =
                      el.offsetLeft - scrollParent.clientWidth / 2 + el.offsetWidth / 2;
                    scrollParent.scrollTo({ left: targetScroll, behavior: 'smooth' });
                  }
                }}
              >
                <div
                  className={cn(
                    'flex h-full items-center justify-center whitespace-nowrap text-sm font-medium leading-5',
                    tabClassName,
                  )}
                >
                  {tab.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);
Tabs.displayName = 'Tabs';

export { Tabs };
